import { useMemo, useState } from 'react';
import { BottomSheet } from '../premium';
import { ChipGrid, PickerChip, SectionLabel } from '../picker/PickerPrimitives';
import { fetchExerciseLibrary } from '../ExerciseNameAutocomplete';

const CATEGORY_ORDER = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Mobility', 'Other'] as const;

const CATEGORIES: { label: (typeof CATEGORY_ORDER)[number]; keywords: string[] }[] = [
  { label: 'Chest', keywords: ['bench', 'chest', 'fly', 'push-up', 'pushup', 'dip', 'pec'] },
  { label: 'Back', keywords: ['row', 'pull', 'lat', 'deadlift', 'pulldown', 'chin', 'back lever'] },
  { label: 'Legs', keywords: ['squat', 'leg', 'lunge', 'calf', 'hamstring', 'quad', 'hip thrust', 'glute'] },
  { label: 'Shoulders', keywords: ['shoulder', 'ohp', 'overhead press', 'lateral', 'raise', 'shrug', 'arnold'] },
  { label: 'Arms', keywords: ['curl', 'tricep', 'bicep', 'extension', 'skull', 'forearm', 'wrist'] },
  { label: 'Core', keywords: ['plank', 'crunch', 'ab', 'core', 'twist', 'hollow', 'sit-up', 'situp', 'leg raise'] },
  {
    label: 'Mobility',
    keywords: [
      'stretch',
      'mobility',
      'foam',
      'circle',
      'rotation',
      'band',
      'warmup',
      'warm-up',
      'ankle',
      'hip flexor',
      '90-90',
      'pigeon',
      'cat cow',
      'child pose',
      'yoga',
      'pilates',
      'arch rock',
      'snatch balance',
    ],
  },
];

const COMMON = [
  'Barbell bench press',
  'Barbell back squat',
  'Conventional deadlift',
  'Overhead press',
  'Barbell row',
  'Lat pulldown',
  'Dumbbell curl',
  'Tricep pushdown',
  'Leg press',
  'Romanian deadlift',
];

type ChipVariant = 'recent' | 'neutral' | 'outline' | 'create';

function categorize(name: string): (typeof CATEGORY_ORDER)[number] {
  const lower = name.toLowerCase();
  for (const c of CATEGORIES) {
    if (c.keywords.some((k) => lower.includes(k))) return c.label;
  }
  return 'Other';
}

function ExerciseSection({
  title,
  names,
  variant = 'neutral',
  onPick,
}: {
  title: string;
  names: string[];
  variant?: ChipVariant;
  onPick: (name: string) => void;
}) {
  if (!names.length) return null;
  return (
    <section>
      <SectionLabel>{title}</SectionLabel>
      <ChipGrid>
        {names.map((name) => (
          <PickerChip key={name} name={name} variant={variant} onClick={() => onPick(name)} />
        ))}
      </ChipGrid>
    </section>
  );
}

export function ExercisePickerSheet({
  open,
  onClose,
  onSelect,
  builtIn,
  custom,
  recent,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (name: string) => void;
  builtIn: string[];
  custom: string[];
  recent: string[];
}) {
  const [query, setQuery] = useState('');
  const [customName, setCustomName] = useState('');

  const allExercises = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of [...builtIn, ...custom, ...COMMON]) {
      const t = n.trim();
      if (t.length < 2) continue;
      map.set(t.toLowerCase(), t);
    }
    return [...map.values()].sort((a, b) => a.localeCompare(b, 'en'));
  }, [builtIn, custom]);

  const trimmedQuery = query.trim();
  const searchActive = trimmedQuery.length > 0;

  const visibleExercises = useMemo(() => {
    if (!searchActive) return allExercises;
    const q = trimmedQuery.toLowerCase();
    return allExercises.filter((n) => n.toLowerCase().includes(q));
  }, [allExercises, searchActive, trimmedQuery]);

  const commonVisible = useMemo(() => {
    if (searchActive) return [];
    return COMMON.filter((name) => allExercises.some((e) => e.toLowerCase() === name.toLowerCase()));
  }, [allExercises, searchActive]);

  const recentVisible = useMemo(() => {
    if (searchActive) return [];
    return recent.slice(0, 8);
  }, [recent, searchActive]);

  const groupedByCategory = useMemo(() => {
    const skip = new Set<string>();
    if (!searchActive) {
      for (const n of [...recentVisible, ...commonVisible]) skip.add(n.toLowerCase());
    }
    const groups = new Map<string, string[]>();
    for (const name of visibleExercises) {
      if (!searchActive && skip.has(name.toLowerCase())) continue;
      const cat = categorize(name);
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(name);
    }
    for (const [key, names] of groups) {
      groups.set(
        key,
        [...names].sort((a, b) => a.localeCompare(b, 'en')),
      );
    }
    return CATEGORY_ORDER.filter((cat) => groups.has(cat)).map((cat) => [cat, groups.get(cat)!] as const);
  }, [visibleExercises, searchActive, recentVisible, commonVisible]);

  function pick(name: string) {
    onSelect(name.trim());
    setQuery('');
    setCustomName('');
    onClose();
  }

  const createLabel = searchActive ? trimmedQuery : customName.trim();
  const canCreate = createLabel.length >= 2;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Add exercise"
      header={
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search exercises"
          enterKeyHint="search"
          className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-base text-[var(--text-primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] outline-none ring-0 placeholder:text-[var(--text-muted)] focus:border-sky-500/35 focus:shadow-[0_0_0_3px_rgba(56,189,248,0.12)]"
        />
      }
    >
      <div className="space-y-5">
        <ExerciseSection title="Recent" names={recentVisible} variant="recent" onPick={pick} />

        <ExerciseSection title="Common" names={commonVisible} variant="neutral" onPick={pick} />

        {searchActive && visibleExercises.length === 0 ? (
          <section>
            <SectionLabel>Search</SectionLabel>
            <p className="mb-3 text-sm text-[var(--text-muted)]">No matches for &ldquo;{trimmedQuery}&rdquo;</p>
            <ChipGrid>
              <PickerChip
                name={`+ Create “${trimmedQuery}”`}
                variant="create"
                onClick={() => pick(trimmedQuery)}
              />
            </ChipGrid>
          </section>
        ) : searchActive ? (
          <ExerciseSection title="Results" names={visibleExercises} variant="neutral" onPick={pick} />
        ) : (
          groupedByCategory.map(([cat, names]) => (
            <ExerciseSection key={cat} title={cat} names={names} variant="neutral" onPick={pick} />
          ))
        )}
      </div>

      <div className="mt-6 border-t border-white/[0.06] pt-5">
        <SectionLabel>Custom exercise</SectionLabel>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Exercise name"
            className="min-w-[min(100%,12rem)] flex-1 rounded-2xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-sky-500/35 focus:shadow-[0_0_0_3px_rgba(56,189,248,0.1)]"
          />
          <button
            type="button"
            disabled={!canCreate && !customName.trim()}
            onClick={() => canCreate && pick(createLabel)}
            className="inline-flex shrink-0 items-center rounded-2xl border border-dashed border-sky-500/35 bg-sky-500/[0.06] px-4 py-2.5 text-sm font-semibold text-sky-200 transition-all hover:border-sky-400/45 hover:bg-sky-500/10 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40"
          >
            + Add custom
          </button>
        </div>
        {!searchActive && customName.trim().length >= 2 ? (
          <div className="mt-3">
            <ChipGrid>
              <PickerChip
                name={`+ Create “${customName.trim()}”`}
                variant="outline"
                onClick={() => pick(customName)}
              />
            </ChipGrid>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}

export async function loadExerciseLibrary() {
  return fetchExerciseLibrary();
}
